import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { Form, Button, Container, Row, Col, Alert } from 'react-bootstrap';
import { getLeadById, createLead, updateLead, patchLead, getSalesRepsWithLeadCount, getClientOrigins, getRegistryLeadTypes, getLeadSubStatuses, logError } from '../api';
import { getUserId } from '../services/AuthService';

const LeadForm = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const isEdit = Boolean(id);

    const [formData, setFormData] = useState({
        name: '',
        phone: '',
        zipCode: '',
        clientOriginId: null,
        originId: null,
        statusId: isEdit ? null : 1,
        subStatusId: null,
        followUpDate: isEdit ? '' : new Date().toISOString().split('T')[0],
        comments: '',
        salesRepId: null,
        createdByUserId: null,
        isUrgent: false
    });

    const [loadingData, setLoadingData] = useState(false);

    const [salesReps, setSalesReps] = useState([]);
    const [clientOrigins, setClientOrigins] = useState([]);
    const [leadStatuses, setLeadStatuses] = useState([]);
    const [leadSubStatuses, setLeadSubStatuses] = useState([]);

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');

    useEffect(() => {
        const loadData = async () => {
            setLoadingData(true);
            try {
                const [salesRepsData, clientOriginsData, leadStatusesData] = await Promise.all([
                    getSalesRepsWithLeadCount(),
                    getClientOrigins(),
                    getRegistryLeadTypes()
                ]);
                setSalesReps(salesRepsData);
                setClientOrigins(clientOriginsData || []);
                setLeadStatuses(leadStatusesData || []);

                if (isEdit) {
                    const leadData = await getLeadById(id);
                    setFormData({
                        name: leadData.name || '',
                        phone: leadData.phone || '',
                        zipCode: leadData.zipCode || '',
                        clientOriginId: leadData.origin?.valueId || null,
                        originId: leadData.originId || null,
                        statusId: leadData.statusId || null,
                        subStatusId: leadData.subStatusId || null,
                        followUpDate: leadData.followUpDate ? 
                            new Date(leadData.followUpDate).toISOString().split('T')[0] : '',
                        comments: leadData.comments || '',
                        salesRepId: leadData.salesRepId || null,
                        createdByUserId: leadData.createdByUserId || null,
                        isUrgent: leadData.isUrgent || false
                    });
                    
                    // Cargar substatus si hay un status seleccionado
                    if (leadData.statusId) {
                        const subStatusesData = await getLeadSubStatuses(leadData.statusId);
                        setLeadSubStatuses(subStatusesData || []);
                    }
                    

                }
            } catch (error) {
                logError(error);
                setError('Error al cargar los datos');
            } finally {
                setLoadingData(false);
            }
        };

        loadData();
    }, [id, isEdit]);

    const handleChange = async (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: value === '' ? null : value
        }));
        
        // Si cambia el status, cargar los substatus correspondientes
        if (name === 'statusId' && value) {
            try {
                const subStatusesData = await getLeadSubStatuses(value);
                setLeadSubStatuses(subStatusesData || []);
                // Limpiar el substatus seleccionado
                setFormData(prev => ({ ...prev, subStatusId: null }));
            } catch (error) {
                console.error('Error loading sub statuses:', error);
                setLeadSubStatuses([]);
            }
        } else if (name === 'statusId' && !value) {
            setLeadSubStatuses([]);
            setFormData(prev => ({ ...prev, subStatusId: null }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');

        try {
            const submitData = {
                ...formData,
                originId: formData.clientOriginId ? parseInt(formData.clientOriginId) : null,
                followUpDate: isEdit ? 
                    (formData.followUpDate ? new Date(formData.followUpDate).toISOString() : null) :
                    new Date().toISOString()
            };

            if (isEdit) {
                await patchLead(id, submitData);
            } else {
                await createLead({ ...submitData, createdByUserId: getUserId() });
            }

            navigate('/lead-assignment');
        } catch (error) {
            logError(error);
            setError(error.message || 'Error al guardar el lead');
        } finally {
            setLoading(false);
        }
    };

    return (
        <Container className="my-4">
            <Row>
                <Col md={8} className="mx-auto">
                    <h2>{isEdit ? 'Editar Lead' : 'Nuevo Lead'}</h2>
                    
                    {error && <Alert variant="danger">{error}</Alert>}
                    
                    {loadingData ? (
                        <div className="text-center py-4">
                            <div className="spinner-border" role="status">
                                <span className="visually-hidden">Cargando...</span>
                            </div>
                            <p className="mt-2">Cargando datos...</p>
                        </div>
                    ) : (
                    <Form onSubmit={handleSubmit}>
                        <Row>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Nombre</Form.Label>
                                    <Form.Control
                                        data-testid="lead-name-input"
                                        type="text"
                                        name="name"
                                        value={formData.name}
                                        onChange={handleChange}
                                        maxLength={50}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={6}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Teléfono *</Form.Label>
                                    <Form.Control
                                        data-testid="lead-phone-input"
                                        type="text"
                                        name="phone"
                                        value={formData.phone}
                                        onChange={handleChange}
                                        required
                                        maxLength={20}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Código Postal</Form.Label>
                                    <Form.Control
                                        data-testid="lead-zipcode-input"
                                        type="text"
                                        name="zipCode"
                                        value={formData.zipCode}
                                        onChange={handleChange}
                                        maxLength={10}
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Origen *</Form.Label>
                                    <Form.Select
                                        data-testid="lead-origin-select"
                                        name="clientOriginId"
                                        value={formData.clientOriginId || ''}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Seleccionar origen</option>
                                        {clientOrigins.map(origin => (
                                            <option key={origin.id} value={origin.valueId}>
                                                {origin.name}
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                            <Col md={4}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Comercial *</Form.Label>
                                    <Form.Select
                                        data-testid="lead-salesrep-select"
                                        name="salesRepId"
                                        value={formData.salesRepId || ''}
                                        onChange={handleChange}
                                        required
                                    >
                                        <option value="">Seleccionar comercial</option>
                                        {salesReps.map(rep => (
                                            <option key={rep.id} value={rep.id} data-testid={`salesrep-option-${rep.id}`}>
                                                {rep.name} {rep.lastName1} ({rep.leadsCount} leads)
                                            </option>
                                        ))}
                                    </Form.Select>
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            {isEdit && (
                                <>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Estado</Form.Label>
                                            <Form.Select
                                                name="statusId"
                                                value={formData.statusId || ''}
                                                onChange={handleChange}
                                            >
                                                <option value="">Seleccionar status</option>
                                                {leadStatuses.map(status => (
                                                    <option key={status.valueId} value={status.valueId}>
                                                        {status.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                    <Col md={4}>
                                        <Form.Group className="mb-3">
                                            <Form.Label>Detalle de Estado</Form.Label>
                                            <Form.Select
                                                name="subStatusId"
                                                value={formData.subStatusId || ''}
                                                onChange={handleChange}
                                                disabled={!formData.statusId || leadSubStatuses.length === 0}
                                            >
                                                <option value="">Seleccionar sub status</option>
                                                {leadSubStatuses.map(subStatus => (
                                                    <option key={subStatus.valueId} value={subStatus.valueId}>
                                                        {subStatus.name}
                                                    </option>
                                                ))}
                                            </Form.Select>
                                        </Form.Group>
                                    </Col>
                                </>
                            )}
                            <Col md={isEdit ? 4 : 12}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Fecha de Seguimiento</Form.Label>
                                    <Form.Control
                                        data-testid="lead-followup-date"
                                        type="date"
                                        name="followUpDate"
                                        value={formData.followUpDate}
                                        onChange={handleChange}
                                        min={isEdit ? undefined : new Date().toISOString().split('T')[0]}
                                        style={{ cursor: 'pointer' }}
                                        onClick={(e) => e.target.showPicker && e.target.showPicker()}
                                    />
                                </Form.Group>
                            </Col>
                        </Row>

                        <Row>
                            <Col md={10}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Comentarios</Form.Label>
                                    <Form.Control
                                        data-testid="lead-comments-textarea"
                                        as="textarea"
                                        rows={3}
                                        name="comments"
                                        value={formData.comments}
                                        onChange={handleChange}
                                        placeholder="Añadir comentarios sobre el lead..."
                                    />
                                </Form.Group>
                            </Col>
                            <Col md={2}>
                                <Form.Group className="mb-3">
                                    <Form.Label>Urgente</Form.Label>
                                    <Form.Check
                                        type="switch"
                                        id="urgent-switch"
                                        name="isUrgent"
                                        checked={formData.isUrgent}
                                        onChange={(e) => setFormData(prev => ({ ...prev, isUrgent: e.target.checked }))}
                                        label={formData.isUrgent ? 'SÍ' : 'NO'}
                                        className="mt-2"
                                    />
                                </Form.Group>
                            </Col>
                        </Row>



                        <div className="d-flex justify-content-between">
                            <Button 
                                data-testid="save-lead-btn"
                                variant="primary" 
                                type="submit"
                                disabled={loading}
                            >
                                {loading ? 'Guardando...' : (isEdit ? 'Actualizar' : 'Crear')}
                            </Button>
                            <Button 
                                data-testid="cancel-lead-btn"
                                variant="secondary" 
                                onClick={() => navigate('/lead-assignment')}
                                disabled={loading}
                            >
                                Cancelar
                            </Button>
                        </div>
                    </Form>
                    )}
                </Col>
            </Row>
        </Container>
    );
};

export default LeadForm;